namespace $.$$ {

	type $shm_projects_version_record = $shm_projects_data_version

	export class $shm_projects_page extends $.$shm_projects_page {

		@ $mol_mem
		override draft_note( next?: string ) {
			return this.$.$shm_projects_draft_note( this.project_id(), next )
		}

		private preview_drag_start: { x: number; y: number } | null = null

		auto() {
			return [
				... super.auto(),
				...( this.version_run_id() ? [ this.version_run_events().native() ] : [] ),
				this.preview_pointer_down_listener(),
				this.preview_pointer_move_listener(),
				this.preview_pointer_up_listener(),
				this.preview_pointer_cancel_listener(),
			]
		}

		project_record() {
			const project = this.$.$shm_projects_data.record( this.project_id() )
			if( !project ) throw new Error( `Unknown project: ${ this.project_id() }` )
			return project
		}

		development_goal() {
			return this.Development() as ReturnType< $shm_projects_page['Development'] > & {
				requirements( next?: string ): string
				rollback_allowed(): boolean
				rollback_to( iteration: number ): void
				run_data( next?: $shm_projects_goal_run | null ): $shm_projects_goal_run | null
				thinking_visible(): boolean
			}
		}

		@ $mol_mem
		override development_output_mode( next?: string ) {
			return this.$.$mol_state_arg.value( 'project_output', next ) ?? 'process'
		}

		development_thinking_visible() {
			return this.development_output_mode() === 'process' && this.development_goal().thinking_visible()
		}

		title() {
			return this.project_record().title
		}

		@ $mol_mem
		project_title_draft( next?: string ) {
			$mol_wire_solid()
			return next ?? this.title()
		}

		@ $mol_action
		override project_title_commit( next?: Event ) {
			if( next === undefined ) return null
			const previous = this.title()
			const title = this.project_title_draft().trim()
			if( !title ) this.project_title_draft( previous )
			else {
				if( title !== previous ) {
					const renamed = $mol_wire_sync( $shm_projects_goal_api ).project_rename( this.project_id(), title )
					this.$.$shm_projects_data.records( renamed.projects )
				}
				this.project_title_draft( title )
			}
			return next
		}

		@ $mol_action
		override project_title_keydown( next?: KeyboardEvent ) {
			if( next?.key !== 'Escape' ) return next ?? null
			next.preventDefault()
			this.project_title_draft( this.title() )
			this.Menu_title().focused( false )
			return next
		}

		menu_title_rows() {
			return [
				this.Menu_title(),
				this.Project_actions(),
			]
		}

		@ $mol_action
		override project_delete( next?: Event ) {
			if( next === undefined ) return null
			const deleted = $mol_wire_sync( $shm_projects_goal_api ).project_delete( this.project_id() )
			this.$.$shm_projects_data.records( deleted.projects )
			this.$.$mol_state_arg.value( 'project', null )
			this.$.$mol_state_arg.value( 'project_tab', null )
			this.$.$mol_state_arg.value( 'project_task', null )
			return next
		}

		section() {
			let section = this.$.$mol_state_arg.value( 'project_tab' )
			if( !section ) section = this.$.$mol_state_arg.value( 'project_tab', 'development' ) || 'development'
			if( this.project_record().directory_pending && section !== 'product' ) return 'development'
			return section
		}

		preview_mode() {
			return this.$.$mol_state_arg.value( 'project_preview' ) === 'true'
		}

		development_mode_current() {
			return !this.preview_mode()
		}

		development_mode_rows() {
			return [
				this.Development_development(),
				...( this.project_record().directory_pending || !$shm_projects_data.preview( this.project_record() ) ? [] : [ this.Development_preview() ] ),
				...( this.rollback_rows().length ? [ this.Development_actions() ] : [] ),
			]
		}

		preview_mode_rows() {
			return [
				this.Preview_development(),
				this.Preview_preview(),
				...( this.rollback_rows().length ? [ this.Development_actions() ] : [] ),
			]
		}

		rollback_rows() {
			const run = this.goal_run()
			if( !run || run.iteration < 1 || run.status === 'queued' || run.status === 'running' || run.status === 'waiting' ) return []
			if( !this.development_goal().rollback_allowed() ) return []
			return [
				this.Rollback( 0 ),
				... Array.from( { length: Math.max( 0, run.iteration - 1 ) }, ( _, index )=> this.Rollback( index + 1 ) ),
			]
		}

		rollback_title( iteration: number ) {
			return iteration ? `Откатить до итерации ${ iteration }` : 'Откатить все'
		}

		@ $mol_action
		override rollback( iteration: number, next?: Event ) {
			if( next === undefined ) return null
			if( iteration ) this.development_goal().rollback_to( iteration + 1 )
			else this.development_goal().rollback_to( 1 )
			return next
		}

		section_arg( section: string ) {
			return { project_tab: section }
		}

		menu_rows() {
			if( this.project_record().directory_pending ) return [
				this.Version_label(),
				this.Section_link( 'product' ),
				this.Section_link( 'development' ),
				this.Section_link( 'tasks' ),
				this.Section_link( 'draft' ),
			]
			return [
				this.Version_label(),
				... [ 'product', 'development', 'tasks', 'docs', 'history', 'draft' ]
					.map( section => this.Section_link( section ) ),
			]
		}

		section_title( section: string ) {
			return {
				product: 'Продукт',
				tasks: 'Задачи',
				development: 'Разработка',
				draft: 'Черновик',
				docs: 'Документация',
				tests: 'Тесты',
				history: 'История версий',
			}[ section ] ?? section
		}

		@ $mol_mem
		override product_description( next?: string ) {
			if( next !== undefined ) return next
			return this.project_record().product?.description ?? ''
		}

		@ $mol_mem
		override product_problem( next?: string ) {
			if( next !== undefined ) return next
			return this.project_record().product?.problem ?? ''
		}

		@ $mol_mem
		override product_audience( next?: string ) {
			if( next !== undefined ) return next
			return this.project_record().product?.audience ?? ''
		}

		@ $mol_mem
		override product_value( next?: string ) {
			if( next !== undefined ) return next
			return this.project_record().product?.value_proposition ?? ''
		}

		@ $mol_mem
		override product_scenarios( next?: string ) {
			if( next !== undefined ) return next
			return this.project_record().product?.scenarios ?? ''
		}

		@ $mol_mem
		override product_success( next?: string ) {
			if( next !== undefined ) return next
			return this.project_record().product?.success_criteria ?? ''
		}

		@ $mol_mem
		override product_stage( next?: string ) {
			if( next !== undefined ) return next
			return this.project_record().product?.stage ?? 'idea'
		}

		@ $mol_action
		override product_save( next?: Event ) {
			if( next === undefined ) return null
			const saved = $mol_wire_sync( $shm_projects_goal_api ).project_product_update( this.project_id(), {
				description: this.product_description(),
				problem: this.product_problem(),
				audience: this.product_audience(),
				value_proposition: this.product_value(),
				scenarios: this.product_scenarios(),
				success_criteria: this.product_success(),
				stage: this.product_stage() as $shm_projects_data_product['stage'],
			} )
			this.$.$shm_projects_data.records( saved.projects )
			const product = saved.project.product
			if( product ) {
				this.product_description( product.description )
				this.product_problem( product.problem )
				this.product_audience( product.audience )
				this.product_value( product.value_proposition )
				this.product_scenarios( product.scenarios )
				this.product_success( product.success_criteria )
				this.product_stage( product.stage )
			}
			return next
		}

		project_pages() {
			const menu = this.Menu_page()
			switch( this.section() ) {
				case 'product': return [ menu, this.Product_page() ]
				case 'tasks': return [ menu, ... this.Tasks().pages_deep() ]
				case 'draft': return [ menu, this.Draft_page() ]
				case 'development': return this.preview_mode()
					? [ menu, this.Preview_page() ]
					: [
						menu,
						this.Development_page(),
						...( this.development_result_visible() ? [ this.Development_output() ] : [] ),
						...( this.development_thinking_visible() ? [ this.Development_thinking() ] : [] ),
					]
				case 'tests': return [ menu, this.Tests_page() ]
				case 'preview': return [ menu, this.Preview_page() ]
				case 'history': return [
					menu,
					this.History_page(),
					...( this.version_selected_id() ? [ this.Version_page() ] : [] ),
				]
				default: return [ menu, this.Docs() ]
			}
		}

		development_stage_rows() {
			const run = this.goal_run()
			if( run?.status !== 'done' ) return []
			if( run.deployments.some( deployment => deployment.stage === 'Пререлиз' && Boolean( deployment.finalized_at ) ) ) return []
			if( run.prerelease?.status === 'queued' || run.prerelease?.status === 'running' ) return []
			const prerelease = run.deployments.find( deployment => deployment.stage === 'Пререлиз' )
			return prerelease ? [ this.Prerelease_summary() ] : [ this.Prerelease_form() ]
		}

		version_current( next?: string ) {
			return this.$.$shm_projects_data.version( this.project_id(), next )
		}

		version_date( next?: string ) {
			return this.$.$shm_projects_data.version_date( this.project_id(), next )
		}

		version_label() {
			const date = this.version_date()
			return `Текущая версия v${ this.version_current() }${ date ? ` (${ date })` : '' }`
		}

		version_next() {
			const parts = this.version_current().split( '.' ).map( Number )
			if( parts.length !== 3 || parts.some( Number.isNaN ) ) return `${ this.version_current() }.1`
			return `${ parts[0] }.${ parts[1] }.${ parts[2] + 1 }`
		}

		version_transition() {
			return `v${ this.version_current() } → v${ this.version_next() }`
		}

		deployment_transition( stage: 'Пререлиз' | 'Релиз' ) {
			const deployment = this.goal_run()?.deployments.find( item => item.stage === stage )
			if( !deployment ) return this.version_transition()
			if( deployment.from_version ) return `v${ deployment.from_version } → v${ deployment.version }`
			const parts = deployment.version.split( '.' ).map( Number )
			if( parts.length !== 3 || parts.some( Number.isNaN ) || parts[2] <= 0 ) return `v${ deployment.version }`
			return `v${ parts[0] }.${ parts[1] }.${ parts[2] - 1 } → v${ deployment.version }`
		}

		prerelease_transition() {
			return this.deployment_transition( 'Пререлиз' )
		}

		release_transition() {
			return this.deployment_transition( 'Релиз' )
		}

		goal_run_id() {
			return this.goal_run()?.id ?? ''
		}

		goal_workspace_id() {
			const workspace = this.goal_run()?.workspace
			return workspace?.active ? workspace.id : 'base'
		}

		@ $mol_mem
		goal_run( next?: $shm_projects_goal_run | null ): $shm_projects_goal_run | null {
			return this.development_goal().run_data( next )
		}

		docs_readme_uri() {
			return this.$.$shm_projects_config.resource_url(
				this.project_record().readme,
				true,
				this.goal_workspace_id(),
			)
		}

		@ $mol_mem
		docs_readme_text() {
			return this.$.$mol_fetch.text( this.docs_readme_uri() )
		}

		docs_agents_uri() {
			return this.$.$shm_projects_config.resource_url(
				this.project_record().agents || 'shm/AGENTS.md',
				true,
				this.goal_workspace_id(),
			)
		}

		@ $mol_mem
		docs_agents_text() {
			return this.$.$mol_fetch.text( this.docs_agents_uri() )
		}

		docs_config_uri() {
			return this.$.$shm_projects_config.resource_url(
				`${ this.project_record().directory }/_shm_project.json`,
				true,
				this.goal_workspace_id(),
			)
		}

		@ $mol_mem
		docs_config_text() {
			const source = this.$.$mol_fetch.text( this.docs_config_uri() ).trimEnd()
			return `\`\`\`json\n${ source }\n\`\`\``
		}

		version_changes( next?: string ) {
			const stored = this.$.$shm_projects_data.version_changes( this.project_id(), next )
			const run = this.goal_run()
			return stored || this.$.$shm_projects_changes_summary( run?.result_history ?? [], run?.results ?? [] ) || run?.summary || ''
		}

		deployment_status( stage: 'Пререлиз' | 'Релиз' ) {
			const deployment = this.goal_run()?.deployments.find( deployment => deployment.stage === stage )
			if( !deployment ) return stage === 'Пререлиз' ? 'Пререлиз ещё не выполнен.' : 'Сначала выполните пререлиз.'
			if( deployment.status === 'failed' ) return `${ stage } не выполнен.`
			if( deployment.server?.startsWith( 'http' ) ) return `${ stage } доступен.`
			if( deployment.status === 'deployed' ) return stage === 'Пререлиз' ? 'Пререлиз подготовлен.' : 'Релиз опубликован.'
			return `${ stage }: бандл подготовлен.`
		}

		prerelease_status() {
			return this.deployment_status( 'Пререлиз' )
		}

		prerelease_uri() {
			return this.goal_run()?.deployments.find( deployment => deployment.stage === 'Пререлиз' )?.server || ''
		}

		release_status() {
			return this.deployment_status( 'Релиз' )
		}

		version_history( next?: readonly $shm_projects_version_record[] ) {
			const stored = this.$.$shm_projects_data.version_history( this.project_id(), next )
			if( next !== undefined ) return stored
			const keys = new Set( stored.map( record => `${ record.stage }:${ record.version }` ) )
			const live = ( this.goal_run()?.deployments ?? [] )
				.map( deployment => deployment.record )
				.filter( record => !keys.has( `${ record.stage }:${ record.version }` ) )
			return [ ...live, ...stored ]
		}

		version_records() {
			return this.version_history()
		}

		project_tests_snapshot(): readonly $shm_projects_data_test[] {
			return ( this.Project_tests() as unknown as {
				version_tests(): readonly $shm_projects_data_test[]
			} ).version_tests()
		}

		version_record( id: string ) {
			return this.version_records_display().find( record => record.id === id ) ?? null
		}

		version_records_display() {
			return this.version_records()
		}

		version_rows() {
			return this.version_records_display().map( record => this.Version_link( record.id ) )
		}

		version_title( id: string ) {
			const record = this.version_record( id )
			if( !record ) return id
			return `v${ record.version } · ${ record.stage }${ record.date && record.date !== '-' ? ` · ${ record.date }` : '' }`
		}

		version_arg( id: string ) {
			return { project_tab: 'history', project_version: id, project_version_output: 'result' }
		}

		version_selected_id() {
			const selected = this.$.$mol_state_arg.value( 'project_version' ) ?? ''
			if( selected && this.version_record( selected ) ) return selected
			return this.version_records_display()[0]?.id ?? ''
		}

		version_selected() {
			return this.version_record( this.version_selected_id() )
		}

		version_selected_title() {
			const record = this.version_selected()
			return record ? `v${ record.version }` : 'Версия'
		}

		@ $mol_mem
		version_mode( next?: string ) {
			return this.$.$mol_state_arg.value( 'project_version_output', next ) ?? 'result'
		}

		version_result_arg() {
			return { project_version_output: 'result' }
		}

		version_session_arg() {
			return { project_version_output: 'session' }
		}

		version_result_current() {
			return this.version_mode() === 'result'
		}

		version_session_current() {
			return this.version_mode() === 'session'
		}

		version_mode_rows() {
			return [
				this.Version_result_mode(),
				...( this.version_run_id() ? [ this.Version_session_mode() ] : [] ),
			]
		}

		version_content() {
			return [ this.version_mode() === 'session' && this.version_run_id() ? this.Version_session() : this.Version_details() ]
		}

		version_selected_date() {
			const record = this.version_selected()
			if( !record ) return ''
			return `${ record.stage }${ record.date && record.date !== '-' ? ` · ${ record.date }` : '' }`
		}

		version_run_id() {
			if( this.section() !== 'history' ) return ''
			return this.version_selected()?.run_id ?? ''
		}

		version_session_run() {
			return this.version_run_data( this.version_run_id() )
		}

		version_session_version() {
			return this.version_selected()?.version ?? ''
		}

		version_session_rows() {
			const run = this.version_session_run()
			if( !run ) return []
			return [
				... Array.from( { length: run.iteration }, ( _, index )=> this.Version_session_chat( `iteration:${ index + 1 }` ).Process_view() ),
				...( run.prerelease ? [ this.Version_session_chat( 'prerelease' ).Process_view() ] : [] ),
			]
		}

		version_session_iteration( id: string ) {
			return id === 'prerelease' ? 0 : Number( id.slice( id.indexOf( ':' ) + 1 ) )
		}

		version_session_prerelease( id: string ) {
			return id === 'prerelease'
		}

		@ $mol_mem_key
		version_run_data( id: string, next?: $shm_projects_goal_run | null ) {
			if( next !== undefined ) return next
			if( !id ) return null
			return this.$.$mol_error_fence(
				()=> $mol_wire_sync( $shm_projects_goal_api ).run( id ),
				()=> null,
				promise => this.$.$mol_fail_hidden( promise ),
			)
		}

		@ $mol_mem
		version_run_events() {
			const events = new this.$.$shm_projects_goal_api_events
			events.id = ()=> this.version_run_id()
			events.notify = ()=> run => this.version_run_receive( run )
			return events
		}

		@ $mol_action
		version_run_receive( run: $shm_projects_goal_run ) {
			this.version_run_data( run.id, run )
			if( run.project ) this.project_record_sync( run.project )
			return run
		}

		project_record_sync( project: $shm_projects_data_record ) {
			const records = this.$.$shm_projects_data.records()
			const index = records.findIndex( record => record.id === project.id )
			if( index < 0 ) return this.$.$shm_projects_data.records([ ...records, project ])
			if( this.$.$mol_compare_deep( records[ index ], project ) ) return records
			const next = [ ...records ]
			next[ index ] = project
			return this.$.$shm_projects_data.records( next )
		}

		version_release_status() {
			const run = this.version_run_data( this.version_run_id() )
			if( !run ) return ''
			if( run.phase === 'Релиз' ) return 'Релиз выполняется...'
			if( run.phase === 'Ошибка релиза' ) return run.error || 'Релиз не завершён.'
			const version = this.version_selected()?.version
			if( run.deployments.some( deployment =>
				deployment.stage === 'Релиз'
				&& deployment.version === version
				&& ( ( deployment as typeof deployment & { channel_project_id?: string } ).channel_project_id || run.project_id ) === this.project_id()
			) ) return 'Релиз опубликован.'
			return ''
		}

		version_action_rows() {
			const record = this.version_selected()
			if( !record || !record.available ) return []
			const status = this.version_release_status()
			if( this.version_run_data( record.run_id ?? '' )?.phase === 'Релиз' ) return status ? [ this.Version_release_state() ] : []
			const channels = this.project_record().channels
			return [
				...( status ? [ this.Version_release_state() ] : [] ),
				...( channels?.prerelease === record.version ? [] : [ this.Version_prerelease() ] ),
				...( channels?.release === record.version ? [] : [ this.Version_release() ] ),
			]
		}

		version_selected_requirements() {
			return this.version_selected()?.requirements || 'Требования для этой версии не зафиксированы.'
		}

		version_selected_changes() {
			const source = this.version_selected()?.changes
			const items = Array.isArray( source ) ? source : String( source || '' ).split( /\r?\n/ )
			const changes: { title: string; description: string; screenshots: string[] }[] = []
			const artifacts = this.screenshot_artifacts()

			for( const item of items ) {
				if( typeof item !== 'string' ) {
					changes.push({
						title: item.title,
						description: item.description || '',
						screenshots: [ ...( item.screenshots ?? [] ) ],
					})
					continue
				}

				const text = item.replace( /^\s*[-*]\s*/, '' ).trim()
				if( !text ) continue
				const screenshot_line = text.match( /^Скриншоты:\s*(.+)$/i )
				const markdown_images = [ ...text.matchAll( /!\[[^\]]*\]\(([^)]+)\)/g ) ].map( match => match[1] )
				if( screenshot_line || markdown_images.length ) {
					if( !changes.length ) continue
					const candidates = screenshot_line ? screenshot_line[1].split( /[,;]/ ) : markdown_images
					for( const candidate of candidates ) {
						const path = candidate.match( /\]\(([^)]+)\)/ )?.[1] ?? candidate
						const normalized = path.trim().replace( /[`'\"]/g, '' ).replace( /\\/g, '/' ).replace( /^\/+/, '' )
						const artifact = artifacts.find( item => normalized === item.path || normalized.endsWith( `/${ item.path }` ) )
						if( artifact && !changes.at( -1 )!.screenshots.includes( artifact.path ) ) changes.at( -1 )!.screenshots.push( artifact.path )
					}
					continue
				}

				changes.push({ title: text, description: '', screenshots: [] })
			}

			if( changes.length && artifacts.length && !changes.some( change => change.screenshots.length ) ) {
				artifacts.forEach( ( artifact, index )=> {
					changes[ Math.min( index, changes.length - 1 ) ].screenshots.push( artifact.path )
				} )
			}

			return changes.length ? changes : [{ title: 'Список изменений не заполнен', description: '', screenshots: [] }]
		}

		change_rows() {
			return this.version_selected_changes().map( ( _, index )=> this.Change( index ) )
		}

		change_content( index: number ) {
			const change = this.version_selected_changes()[ index ]
			if( !change ) return []
			return [
				this.Change_title( index ),
				...( change.description ? [ this.Change_description( index ) ] : [] ),
				...( change.screenshots.length ? [ this.Change_screenshots( index ) ] : [] ),
			]
		}

		change_title( index: number ) {
			return this.version_selected_changes()[ index ]?.title || ''
		}

		change_description( index: number ) {
			return this.version_selected_changes()[ index ]?.description || ''
		}

		change_screenshot_rows( change: number ) {
			return this.version_selected_changes()[ change ]?.screenshots.map( ( _, screenshot )=> this.Change_screenshot( `${ change }:${ screenshot }` ) ) ?? []
		}

		change_screenshot_uri( key: string ) {
			const [ change, screenshot ] = key.split( ':' ).map( Number )
			const path = this.version_selected_changes()[ change ]?.screenshots[ screenshot ] || ''
			return path ? this.$.$shm_projects_config.resource_url( path ) : ''
		}

		version_artifacts() {
			return this.version_selected()?.artifacts ?? []
		}

		screenshot_artifacts() {
			return this.version_artifacts().filter( artifact => artifact.kind === 'screenshot' )
		}

		other_artifacts_data() {
			return this.version_artifacts().filter( artifact => artifact.kind === 'video' )
		}

		other_artifact_rows() {
			const artifacts = this.other_artifacts_data()
			return artifacts.length ? artifacts.map( ( _, index )=> this.Other_artifact( index ) ) : [ this.No_other_artifacts() ]
		}

		other_artifact_title( index: number ) {
			return this.other_artifacts_data()[ index ]?.title || ''
		}

		other_artifact_uri( index: number ) {
			const path = this.other_artifacts_data()[ index ]?.path || ''
			return path ? this.$.$shm_projects_config.resource_url( path ) : ''
		}

		version_tests_data() {
			return this.version_selected()?.tests ?? []
		}

		version_tests_title() {
			return 'Проверки'
		}

		version_test_rows() {
			const tests = this.version_tests_data()
			return tests.length ? tests.map( ( _, index )=> this.Version_test( index ) ) : []
		}

		version_test_status( index: number ) {
			return this.version_tests_data()[ index ]?.status === 'passed' ? '✓' : '✕'
		}

		version_test_title( index: number ) {
			return this.version_tests_data()[ index ]?.title || ''
		}

		version_test_uri( index: number ) {
			const test = this.version_tests_data()[ index ]
			if( !test ) return ''
			const app = this.$.$shm_projects_config.preview_url( 'shm/tests/app' )
			return `${ app }#!test_scenario=${ encodeURIComponent( test.scenario ) }/test_run=${ encodeURIComponent( test.run ) }`
		}

		goal_artifacts(): readonly $shm_projects_data_artifact[] {
			return ( this.goal_run()?.artifacts ?? [] ).filter( artifact => artifact.type !== 'report' ).map( ( artifact, index )=> ({
				kind: artifact.type,
				title: `${ artifact.type === 'screenshot' ? 'Скриншот' : artifact.type === 'video' ? 'Видео' : 'Отчёт' } ${ index + 1 }`,
				path: artifact.path,
			}) )
		}

		goal_requirements() {
			const run = this.goal_run()
			return run
				? [ run.goal, ...run.refinements ].filter( Boolean ).join( '\n\n' )
				: this.$.$shm_projects_data.requirements( this.project_id() )
		}

		new_version_tests( version = this.version_next() ) {
			const known = new Set(
				this.version_records()
					.filter( record => record.version !== version )
					.flatMap( record => ( record.tests ?? [] ).map( test => test.scenario ) ),
			)
			return this.project_tests_snapshot().filter( ( test: $shm_projects_data_test )=> !known.has( test.scenario ) )
		}

		@ $mol_action
		override prerelease_make( next?: Event ) {
			if( next === undefined ) return null
			const run_id = this.goal_run_id()
			if( !run_id ) return next
			const run = $mol_wire_sync( $shm_projects_goal_api ).deploy( run_id, 'prerelease', this.version_changes() )
			this.goal_run( run )
			this.$.$mol_state_arg.value( 'goal_iteration', 'prerelease' )
			this.development_output_mode( run.prerelease?.status === 'done' ? 'result' : 'process' )
			return next
		}

		@ $mol_action
		override release_make( next?: Event ) {
			if( next === undefined ) return null
			const record = this.version_selected()
			if( !record ) return next
			const run = record.available
				? $mol_wire_sync( $shm_projects_goal_api ).version_activate( this.project_id(), record.version, 'release' )
				: $mol_wire_sync( $shm_projects_goal_api ).deploy( record.run_id ?? '', 'release', '' )
			this.version_run_receive( run )
			const deployment = run.deployments.find( deployment => deployment.stage === 'Релиз' )
			if( deployment ) this.$.$mol_state_arg.value( 'project_version', deployment.record.id )
			return next
		}

		@ $mol_action
		override prerelease_activate( next?: Event ) {
			if( next === undefined ) return null
			const record = this.version_selected()
			if( !record?.available ) return next
			const run = $mol_wire_sync( $shm_projects_goal_api ).version_activate( this.project_id(), record.version, 'prerelease' )
			this.version_run_receive( run )
			return next
		}

		version_record_add(
			stage: 'Пререлиз' | 'Релиз',
			version = this.version_next(),
			date = new Date().toLocaleDateString( 'ru-RU' ),
			changes = this.version_changes(),
			snapshot?: $shm_projects_version_record,
		) {
			const record: $shm_projects_version_record = snapshot ?? {
				id: $mol_guid(),
				version,
				date,
				requirements: this.goal_requirements() || 'Требования не зафиксированы',
				changes: changes.split( /\r?\n/ ).map( item => item.replace( /^\s*[-*]\s*/, '' ).trim() ).filter( Boolean ),
				stage,
				artifacts: this.goal_artifacts(),
				tests: this.new_version_tests( version ),
			}
			this.version_history( [ record, ...this.version_history() ] )
			return record
		}

		preview_url() {
			const project = this.project_record()
			const path = $shm_projects_data.preview( project )
			return this.preview_url_with_token( $shm_projects_config.preview_url( path, this.goal_workspace_id() ) )
		}

		preview_open_uri() {
			const uri = this.preview_url()
			return uri.startsWith( 'about:' ) ? '' : uri
		}

		preview_url_with_token( uri: string ) {
			const token = $shm_projects_config.agent_token()
			if( !token || uri.startsWith( 'about:' ) ) return uri

			try {
				const url = new URL( uri, this.$.$mol_dom_context.document?.location.href )
				url.searchParams.set( 'token', token )
				return url.toString()
			} catch {
				return uri
			}
		}

		@ $mol_mem
		preview_pointer_blocked( next?: boolean ) {
			return next ?? false
		}

		preview_pointer_events() {
			return this.preview_pointer_blocked() ? 'none' : 'auto'
		}

		@ $mol_mem
		preview_pointer_down_listener() {
			return new $mol_dom_listener(
				this.$.$mol_dom_context.document,
				'pointerdown',
				$mol_wire_async( event => this.preview_pointer_down( event as PointerEvent ) ),
				{ passive: true, capture: true } as any,
			)
		}

		@ $mol_mem
		preview_pointer_move_listener() {
			return new $mol_dom_listener(
				this.$.$mol_dom_context.document,
				'pointermove',
				$mol_wire_async( event => this.preview_pointer_move( event as PointerEvent ) ),
				{ passive: true },
			)
		}

		@ $mol_mem
		preview_pointer_up_listener() {
			return new $mol_dom_listener(
				this.$.$mol_dom_context.document,
				'pointerup',
				$mol_wire_async( ()=> this.preview_pointer_end() ),
				{ passive: true },
			)
		}

		@ $mol_mem
		preview_pointer_cancel_listener() {
			return new $mol_dom_listener(
				this.$.$mol_dom_context.document,
				'pointercancel',
				$mol_wire_async( ()=> this.preview_pointer_end() ),
				{ passive: true },
			)
		}

		preview_pointer_down( event?: PointerEvent ) {
			if( !event || event.button !== 0 || this.preview_event_inside( event ) ) return
			this.preview_drag_start = { x: event.clientX, y: event.clientY }
			this.preview_pointer_blocked( false )
		}

		preview_pointer_move( event?: PointerEvent ) {
			const start = this.preview_drag_start
			if( !event || !start || this.preview_pointer_blocked() ) return
			const dx = event.clientX - start.x
			const dy = event.clientY - start.y
			if( dx * dx + dy * dy < 25 ) return
			this.preview_pointer_blocked( true )
		}

		preview_pointer_end() {
			this.preview_drag_start = null
			this.preview_pointer_blocked( false )
		}

		preview_event_inside( event: PointerEvent ) {
			const target = event.target as Node | null
			return target ? this.Preview().dom_node_actual().contains( target ) : false
		}

	}

}
